import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import { Event, EventOption } from 'types/calendar';
import Translations from 'App/Settings/Components/Translations';
import Modal from 'Components/Molecules/Modal';
import ImageField from 'Components/Organisms/ImageField';
import TextField from 'Components/Atoms/TextField';
import { Translation } from 'utils/types';
import IconButton from 'Components/Atoms/IconButton';
import { Close } from 'Components/Atoms/Icons';
import { Severity } from 'Contexts/RestaurantContext';
import useToast from 'CustomHooks/useToast';
import InputAdornmentAtom from 'Components/Atoms/InputAdornment';
import Tooltip from 'Components/Atoms/Tooltip';
import useRestaurantVatCountry from 'CustomHooks/useRestaurantVatCountry';
import useRestaurant from 'CustomHooks/useRestaurant';
import { toVatCents } from 'types/shared/helper/eventVatComponents';
import VatSplitEditor from './VatSplitEditor';
import { vatSplitSaveError } from './vatSplitSaveError';
import { hasTicketCategories } from 'types/shared/helper/ticketCategories';
import './edit-option-modal.scss';

type EditOptionModalProps = {
  openEditOptions:
    | false
    | {
        isOpen: boolean;
        optId: string | null;
        type: 'options' | 'addons';
      };
  setOpenEditOptions: React.Dispatch<
    React.SetStateAction<
      | false
      | {
          isOpen: boolean;
          optId: string | null;
          type: 'options' | 'addons';
        }
    >
  >;
  setEventState: React.Dispatch<React.SetStateAction<Event | null>>;
  eventState: Event | null;
};

const EditOptionModal = ({
  openEditOptions,
  setOpenEditOptions,
  setEventState,
  eventState,
}: EditOptionModalProps) => {
  const toast = useToast();
  const { t } = useTranslation('calendar');
  const vatCountry = useRestaurantVatCountry();
  const pricedByCategory = hasTicketCategories(eventState) && openEditOptions && openEditOptions.type === 'options';
  const { restaurantId } = useRestaurant();
  const [vatPending, setVatPending] = useState<string | null>(null);

  const [state, setstate] = useState<EventOption | undefined>();

  useEffect(() => {
    if (
      openEditOptions &&
      openEditOptions.optId &&
      openEditOptions.optId !== state?.id
    ) {
      setstate(
        eventState?.options?.find((o) => o.id === openEditOptions.optId)
      );
    } else if (!openEditOptions) {
      setstate(undefined);
    }
  }, [openEditOptions]);

  const originalOption = useMemo(
    () =>
      eventState &&
      eventState.options &&
      openEditOptions &&
      [...eventState.options].find((o) => o.id === openEditOptions.optId),
    [openEditOptions]
  );

  const modifyOption = (toEditOption: EventOption) => {
    if (eventState?.options && openEditOptions) {
      const filteredOptions = [...eventState?.options].filter(
        (o) => o.id !== openEditOptions.optId
      );

      setEventState({
        ...eventState,
        options: [...filteredOptions, toEditOption],
      });
    }
  };

  const handleCancel = () => {
    if (openEditOptions && openEditOptions.optId === 'newOpt') {
      setEventState((eS) =>
        eS
          ? { ...eS, options: eS.options?.filter((o) => o.id !== 'newOpt') }
          : eS
      );
    }

    setOpenEditOptions(false);
  };

  const handleSave = () => {
    console.log({
      state,
      eventState,
    });

    if (state) {
      const internalName =
        state.internalName ||
        state.translation?.de?.title ||
        state.translation?.en?.title ||
        '';

      if (!internalName) {
        return toast(
          'Der Interne Name ist ein Pflichtfeld, bitte geben Sie einen Namen ein.',
          Severity.ERROR,
          'common'
        );
      }

      // The split covers the full price: event base price plus option amount.
      // With price categories each category splits its own price, an option
      // only its surcharge.
      const vatError = vatSplitSaveError(
        state,
        (pricedByCategory ? 0 : eventState?.amount ?? 0) + (state.amount ?? 0),
        vatCountry.country,
        vatPending,
        t
      );
      if (vatError) {
        // Already translated: no second lookup (names may contain "_").
        return toast(vatError, Severity.ERROR, null);
      }

      setEventState((eS) => {
        if (eS) {
          return {
            ...eS,
            options: eS.options?.map((o) => {
              if (o.id === state.id) {
                return {
                  ...state,
                  id: state.id === 'newOpt' ? `${Date.now()}` : state.id,
                  internalName,
                };
              }
              return o;
            }),
          };
        }
        return eS;
      });
      setOpenEditOptions(false);
    }
  };

  //INTERNALNAME
  const handleNameChange = (name: string) => {
    if (eventState?.options && openEditOptions) {
      let toEditOption: any = {
        ...eventState.options.find((o) => o.id === openEditOptions.optId),
      };

      toEditOption.internalName = name;

      modifyOption(toEditOption);
    }
  };

  const handleVATChange = (rate: string) => {
    if (eventState?.options && openEditOptions) {
      let toEditOption: any = {
        ...eventState.options.find((o) => o.id === openEditOptions.optId),
      };

      toEditOption.vatRate = rate;
      modifyOption(toEditOption);
    }
  };

  const handleOptTitleAndDescriptionChange = (
    e: Record<string, Translation<string>>
  ) => {
    const newText: Record<
      string,
      {
        title: string;
        description: string;
      }
    > = Object.entries(e).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {}
    );

    if (eventState && eventState.options && openEditOptions) {
      let toEditOption: any = {
        ...eventState.options.find((o) => o.id === openEditOptions.optId),
      };

      toEditOption.translation = newText;

      modifyOption(toEditOption);
    }
  };

  const handleOptionType = (type: EventOption['type']) => {
    if (eventState?.options && openEditOptions) {
      let toEditOption: any = {
        ...eventState.options.find((o) => o.id === openEditOptions.optId),
      };

      toEditOption.type = type;

      modifyOption(toEditOption);
    }
  };

  const handleOptionImage = (e: string | (string | null)[] | null) => {
    if (eventState?.options && openEditOptions) {
      let toEditOption: any = {
        ...eventState.options.find((o) => o.id === openEditOptions.optId),
      };

      if (typeof e === 'string') toEditOption.img = e;

      modifyOption(toEditOption);
    }
  };

  const handleOptionLimited = (e: number) => {
    if (eventState?.options && openEditOptions) {
      let toEditOption: any = {
        ...eventState.options.find((o) => o.id === openEditOptions.optId),
      };

      toEditOption.limited = e;

      modifyOption(toEditOption);
    }
  };

  const handleOptionPrice = (e: number) => {
    if (openEditOptions) {
      setEventState((eS) => {
        if (eS) {
          return {
            ...eS,
            options: eS.options?.map((o) => {
              if (o.id === (openEditOptions?.optId ?? '')) {
                return {
                  ...o,
                  amount: e,
                };
              }
              return o;
            }),
          };
        }
        return eS;
      });
    }
  };

  const handleChange = (fn: (s: EventOption) => EventOption) => {
    setstate((x) => (!!x ? fn(x) : x));
  };


  if (!eventState) {
    return <></>;
  }

  return (
    <Modal
      title="Option"
      titleTranslation="calendar"
      maxWidth="lg"
      open={!!openEditOptions && openEditOptions.type === 'options'}
      headerRight={
        <Box flex style={{ justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleSave}
            style={{ marginRight: 16 }}
            typographyProps={{ variant: 'text-3' }}
          >
            Save
          </Button>
          <IconButton size="small" onClick={handleCancel}>
            <Close color="inherit" />
          </IconButton>
        </Box>
      }
    >
      {state && (
        <div className="edit-option">
          <Box className="mg-bt-md">
            <Typography
              className="mg-bt-xs"
              variant="h5"
              translation="calendar"
            >
              Edit or add an option to this event
            </Typography>
            <Typography color="subdued" variant="text-3" translation="calendar">
              You have to choose a name for your option, for you to recognize it
              easily (18 characters max). Then you can modify its details.
            </Typography>
          </Box>

          <Box className="mg-bt-lg edit-option__name">
            <TextField
              fullWidth
              required
              label="Choose an internal name"
              value={state.internalName || ''}
              onChange={(e) =>
                handleChange((s) => ({
                  ...s,
                  internalName: e.target.value,
                }))
              }
              style={{ minWidth: '46%' }}
              labelTranslation="calendar"
              inputProps={{ maxLength: 18 }}
            />
          </Box>

          <Box flex className="mg-bt-lg edit-option__texts">
            <Box className="max-column-width">
              <Translations
                borders
                state={{
                  ...state.translation,
                  de: {
                    title: state.translation.de.title || '',
                    description: state.translation.de.description || '',
                  },
                  en: {
                    title: state.translation.en.title || '',
                    description: state.translation.en.description || '',
                  },
                }}
                onChange={(t) =>
                  handleChange((s) => ({
                    ...s,
                    translation: t as any,
                  }))
                }
                keys={[
                  {
                    id: 'title',
                    label: 'Title',
                    required: true,
                  },
                  {
                    id: 'description',
                    label: 'Description',
                    required: true,
                    multiline: true,
                  },
                ]}
              />
            </Box>
            <ImageField
              value={state?.img || ''}
              onChange={(e) =>
                handleChange((x) => ({
                  ...x,
                  img: Array.isArray(e) ? e?.[0] ?? '' : e ?? '',
                }))
              }
              label={''}
              type="url"
            />
          </Box>

          <Box
            fullSize
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
            }}
            className="mg-bt-md"
          >
            <Box flex className="edit-option__price-row" style={{ marginBottom: 8 }}>
              <Box>
                <Box flex>
                  <Typography
                    style={{ minWidth: 'fit-content', marginBottom: 4 }}
                    variant="text-3"
                    translation="calendar"
                    block
                  >
                    Price and VAT
                  </Typography>
                  <Tooltip
                    style={{ marginLeft: 4 }}
                    content={
                      <Typography variant="text-5" translation="calendar">
                        The price includes VAT. If a different rate applies to parts of the price (e.g. food and drinks), turn on the split across several VAT rates.
                      </Typography>
                    }
                  >
                    <Typography variant="text-3" color="subdued">
                      🛈
                    </Typography>
                  </Tooltip>
                </Box>
                <Box flex className="mg-bt-sm">
                  <TextField
                    id="option-price"
                    fullWidth
                    type="number"
                    label="Option price"
                    labelTranslation="calendar"
                    value={state?.amount || ''}
                    onChange={(e) => {
                      // Read now: a queued update may only run after React
                      // restored the controlled input to its previous value.
                      const amount = Number(e.target.value);
                      handleChange((x) => ({ ...x, amount }));
                    }}
                    // style={{ minWidth: "46%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornmentAtom position="end">
                          <Typography color="subdued">€</Typography>
                        </InputAdornmentAtom>
                      ),
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography
                  block
                  variant="text-3"
                  translation="calendar"
                  style={{ marginBottom: 4 }}
                >
                  Quantity
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  label="Specify limited quantity"
                  labelTranslation="calendar"
                  value={state?.limited || ''}
                  onChange={(e) =>
                    handleChange((x) => ({
                      ...x,
                      limited: Number(e.target.value),
                    }))
                  }
                />
              </Box>
            </Box>
          </Box>
          <Box className="mg-bt-md" style={{ maxWidth: 720 }}>
            <VatSplitEditor
              idPrefix="option"
              grossCents={toVatCents((pricedByCategory ? 0 : eventState.amount ?? 0) + (state.amount ?? 0))}
              priceHint={
                pricedByCategory
                  ? 'nur der Aufpreis der Option; den Ticketpreis teilt jede Preiskategorie selbst auf'
                  : eventState.amount
                    ? t('calendar_Event base price + option amount')
                    : undefined
              }
              vatRate={state.vatRate}
              vatComponents={state.vatComponents}
              country={vatCountry.country}
              countryLoaded={vatCountry.loaded}
              storedCountry={vatCountry.stored}
              restaurantId={restaurantId ?? ''}
              onChange={(vat) => handleChange((x) => ({ ...x, ...vat }))}
              onPendingChange={setVatPending}
            />
          </Box>
        </div>
      )}
    </Modal>
  );
};

export default EditOptionModal;
